import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, List, Avatar, Input, Button, Empty, Badge, Spin, message } from 'antd';
import { SendOutlined, UserOutlined } from '@ant-design/icons';
import { get, post, isAuthenticated } from '../utils/api';
import './css/Messages.css';

const { Sider, Content } = Layout;

const Messages = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const messagesEndRef = useRef(null);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!isAuthenticated()) {
      message.warning('请先登录后再查看消息');
      navigate('/login');
      return;
    }

    fetchConversations();
    fetchUnreadCount();

    // Set up polling for new messages and unread count
    const intervalId = setInterval(() => {
      if (currentConversation) {
        fetchMessages(currentConversation.user.id);
      }
      fetchUnreadCount();
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (userId && isAuthenticated()) {
      setLoadingConversation(true);
      fetchMessages(userId);
    }
  }, [userId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const data = await get('/api/messaging/conversations/');
      setConversations(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      message.error('获取对话列表失败');
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const data = await get('/api/messaging/messages/unread-count/');
      setUnreadCount(data.unread_count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const fetchMessages = async (otherUserId) => {
    try {
      const data = await get(`/api/messaging/conversations/${otherUserId}/`);
      
      setMessages(data.messages);
      setCurrentConversation({
        id: data.conversation_id,
        user: data.user
      });
      setLoadingConversation(false);
      
      // Refresh conversations list to update unread counts
      fetchConversations();
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      message.error('获取消息失败');
      setLoadingConversation(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !currentConversation) {
      return;
    }

    setSendingMessage(true);
    try {
      await post('/api/messaging/messages/create/', {
        recipient_id: currentConversation.user.id,
        content: messageText
      });
      
      setMessageText('');
      
      // Refresh messages
      fetchMessages(currentConversation.user.id);
    } catch (error) {
      console.error('Failed to send message:', error);
      message.error('发送消息失败');
    } finally {
      setSendingMessage(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN');
  };

  if (loading) {
    return (
      <div className="messages-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="messages-container">
      <Layout className="messages-layout">
        <Sider width={300} className="conversations-sider">
          <div className="conversations-header">
            <h2>站内消息 {unreadCount > 0 && <Badge count={unreadCount} />}</h2>
          </div>
          {conversations.length > 0 ? (
            <List
              className="conversations-list"
              dataSource={conversations}
              renderItem={item => (
                <List.Item
                  className={`conversation-item ${currentConversation && item.id === currentConversation.id ? 'active' : ''}`}
                  onClick={() => {
                    // Find the other participant (not the current user)
                    const otherParticipant = item.participants.find(p => p.username !== localStorage.getItem('username'));
                    if (otherParticipant) {
                      fetchMessages(otherParticipant.id);
                    } else {
                      // Fallback to first participant if can't find other user
                      fetchMessages(item.participants[0].id);
                    }
                  }}
                >
                  <List.Item.Meta
                    avatar={
                      <Badge dot={item.unread_count > 0}>
                        <Avatar 
                          src={item.participants.find(p => p.username !== localStorage.getItem('username'))?.profile_picture_url} 
                          icon={<UserOutlined />}
                        />
                      </Badge>
                    }
                    title={item.participants.find(p => p.username !== localStorage.getItem('username'))?.username || item.participants[0].username}
                    description={
                      item.last_message ? 
                        (item.last_message.content.length > 20 ? 
                          `${item.last_message.content.substring(0, 20)}...` : 
                          item.last_message.content) : 
                        '没有消息'
                    }
                  />
                  {item.last_message && (
                    <div className="conversation-time">
                      {formatTime(item.last_message.created_at)}
                    </div>
                  )}
                </List.Item>
              )}
            />
          ) : (
            <Empty description="暂无对话" />
          )}
        </Sider>
        <Content className="messages-content">
          {currentConversation ? (
            <>
              <div className="messages-header">
                <h2>{currentConversation.user.username}</h2>
              </div>
              <div className="messages-list">
                {loadingConversation ? (
                  <div className="messages-loading-conversation">
                    <Spin />
                  </div>
                ) : messages.length > 0 ? (
                  <div className="messages-list-content">
                    {messages.map((msg, index) => {
                      const isFirstOfDay = index === 0 || 
                        formatDate(msg.created_at) !== formatDate(messages[index - 1].created_at);
                      
                      console.log(`Rendering message ${index}:`, msg); // Debug log for each message
                      
                      return (
                        <React.Fragment key={msg.id}>
                          {isFirstOfDay && (
                            <div className="message-date-divider">
                              <span>{formatDate(msg.created_at)}</span>
                            </div>
                          )}
                          <div className={`message-item ${msg.sender.username !== localStorage.getItem('username') ? 'received' : 'sent'}`}>
                            <div className="message-avatar">
                              <Avatar 
                                src={msg.sender.profile_picture_url || null} 
                                icon={<UserOutlined />}
                              />
                            </div>
                            <div className="message-content">
                              <div className="message-sender">
                                {msg.sender.username}
                              </div>
                              <div className="message-bubble">{msg.content}</div>
                              <div className="message-time">{formatTime(msg.created_at)}</div>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                ) : (
                  <Empty description="暂无消息" />
                )}
              </div>
              <div className="message-input-container">
                <Input.TextArea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="输入消息..."
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={sendingMessage}
                  onClick={handleSendMessage}
                  disabled={!messageText.trim()}
                  size="large"
                >
                  发送
                </Button>
              </div>
            </>
          ) : (
            <div className="no-conversation-selected">
              <Empty description="选择一个对话或开始新的对话" />
            </div>
          )}
        </Content>
      </Layout>
    </div>
  );
};

export default Messages;
